import React, { useState, useEffect } from 'react';
import { signOut } from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { auth, db, functions } from '../firebase';

// ═══════════════════════════════════════════════════════════════════════════
// LENGKAPKAN PENDAFTARAN — gate untuk pengguna BARU yang daftar via Google.
// Google tak kutip No. WhatsApp & tak semak pautan ejen, jadi skrin ni:
//   1. Pastikan ada rujukan ejen (cookie sesaat_ref ATAU referredBy sedia ada) — tiada = sekat.
//   2. Kutip No. WhatsApp (wajib, untuk reminder pembaharuan pakej).
// Dirender oleh App.jsx bila akaun google-only + baru + belum ada whatsappNumber.
// ═══════════════════════════════════════════════════════════════════════════

const ADMIN_WA_NUMBER = '601135011231';
const ADMIN_WA_MESSAGE = 'Salam admin, saya ingin mendaftar SESAAT tetapi tiada rujukan dari ejen. Tolong bantu.';

// Baca kod ejen (sama macam Login.jsx): cookie → localStorage → alamat URL.
// Alamat dibaca sbg jaring terakhir — pelanggan yang bertukar browser
// (WhatsApp → Safari) tinggalkan cookie di belakang. Lihat utils/refLink.js.
import { getActiveRef } from '../utils/refLink';

// Normalize nombor MY ke +60XXXXXXXXX (sama macam Login.jsx)
function normalizeMyPhone(raw) {
    if (!raw) return '';
    const digits = raw.replace(/\D/g, '');
    if (digits.startsWith('60')) return `+${digits}`;
    if (digits.startsWith('0')) return `+60${digits.slice(1)}`;
    return `+60${digits}`;
}

function isValidMyPhone(raw) {
    const normalized = normalizeMyPhone(raw);
    return /^\+60\d{9,10}$/.test(normalized);
}

const CompleteRegistration = ({ user, onCompleted }) => {
    // status: loading | form | blocked
    const [status, setStatus] = useState('loading');
    const [refCode, setRefCode] = useState(null);       // kod ejen (cookie atau doc)
    const [refFromDoc, setRefFromDoc] = useState(false); // true = referredBy dah terikat dlm doc
    const [whatsapp, setWhatsapp] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    // Semak rujukan ejen: cookie DULU, fallback referredBy dlm users doc
    // (auth listener App.jsx mungkin dah bind referredBy sebelum gate ni render).
    useEffect(() => {
        let cancelled = false;
        (async () => {
            const cookieRef = getActiveRef();
            let docRef = null;
            try {
                const snap = await getDoc(doc(db, 'users', user.uid));
                docRef = snap.data()?.referredBy || null;
            } catch (_) { /* doc tak wujud / gagal baca — cookie sahaja */ }
            if (cancelled) return;
            const ref = cookieRef || docRef;
            if (ref) {
                setRefCode(ref);
                setRefFromDoc(!!docRef);
                setStatus('form');
            } else {
                setStatus('blocked');
            }
        })();
        return () => { cancelled = true; };
    }, [user.uid]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!isValidMyPhone(whatsapp)) {
            setError('Nombor WhatsApp tidak sah. Contoh: 0123456789 atau +60123456789');
            return;
        }
        setError('');
        setSaving(true);
        try {
            const profile = {
                email: user.email,
                whatsappNumber: normalizeMyPhone(whatsapp),
            };
            await setDoc(doc(db, 'users', user.uid), profile, { merge: true });
            // referredBy ditulis ASING: rules benarkan ia diisi SEKALI sahaja (bila kosong). Listener
            // App.jsx / bindReferral mungkin sudah mengisinya selepas page ini dibuka — tulisan ini
            // ditolak, tetapi WhatsApp di atas tetap tersimpan.
            // ⚠️ KEMBAR: firestore.rules users update (referredBy write-once) · Login.jsx.
            if (refCode && !refFromDoc) {
                try {
                    await setDoc(doc(db, 'users', user.uid), { referredBy: refCode }, { merge: true });
                } catch (refDbErr) {
                    console.warn('[CompleteRegistration] referredBy not saved (already set):', refDbErr?.message);
                }
            }
            // Pastikan createdAt wujud (akaun Google baru — listener upsert mungkin dah set)
            try {
                const snap = await getDoc(doc(db, 'users', user.uid));
                if (!snap.data()?.createdAt) {
                    await setDoc(doc(db, 'users', user.uid), { createdAt: serverTimestamp() }, { merge: true });
                }
            } catch (_) {}
            // Bind referral server-side (atomic; non-fatal — listener App.jsx ada fallback)
            if (refCode && !refFromDoc) {
                try {
                    await httpsCallable(functions, 'bindReferral')({ refCode });
                } catch (refErr) {
                    console.warn('[CompleteRegistration] bindReferral gagal (non-fatal):', refErr?.message);
                }
            }
            onCompleted();
        } catch (err) {
            console.error('[CompleteRegistration] Gagal simpan profil:', err);
            setError('Gagal menyimpan. Sila cuba lagi.');
            setSaving(false);
        }
    };

    const handleSignOut = async () => {
        try { await signOut(auth); } catch (_) {}
    };

    return (
        <div className="creg-page">
            <link href="https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800&display=swap" rel="stylesheet" />
            <div className="creg-card">
                {/* Branding */}
                <div className="creg-branding">
                    <div className="creg-logo-icon">
                        <span className="material-symbols-outlined" style={{ fontSize: 18, color: '#fff', fontVariationSettings: "'FILL' 1" }}>bolt</span>
                    </div>
                    <span className="creg-brand-name">SESAAT</span>
                </div>

                {status === 'loading' && (
                    <div style={{ textAlign: 'center', padding: '30px 0' }}>
                        <span className="creg-spinner" style={{ borderTopColor: '#60a5fa', width: 28, height: 28 }}></span>
                    </div>
                )}

                {status === 'form' && (
                    <>
                        <h2 className="creg-title">Lengkapkan Pendaftaran</h2>
                        <p className="creg-subtitle">
                            Satu langkah terakhir sebelum masuk. Masukkan No. WhatsApp anda.
                        </p>

                        <div className="creg-ref-ok">
                            <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>verified</span>
                            <span>Anda dirujuk oleh ejen: <strong>{refCode}</strong></span>
                        </div>

                        {error && (
                            <div className="creg-msg-error">
                                <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>error</span>
                                {error}
                            </div>
                        )}

                        <form onSubmit={handleSubmit} className="creg-form">
                            <input
                                type="tel"
                                value={whatsapp}
                                onChange={(e) => setWhatsapp(e.target.value)}
                                className="creg-input"
                                placeholder="No. WhatsApp (cth: 0123456789)"
                                required
                                autoComplete="tel"
                            />
                            <p style={{ fontSize: 11, color: '#64748b', margin: '-4px 0 0', lineHeight: 1.4, textAlign: 'left' }}>
                                Kami akan guna WhatsApp untuk hantar reminder pembaharuan pakej.
                            </p>
                            <button type="submit" disabled={saving} className="creg-submit-btn">
                                {saving ? <span className="creg-spinner"></span> : 'Siapkan Pendaftaran'}
                            </button>
                        </form>

                        <button type="button" onClick={handleSignOut} className="creg-back-link">
                            <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>logout</span>
                            Log keluar (tukar akaun)
                        </button>
                    </>
                )}

                {status === 'blocked' && (
                    <>
                        <h2 className="creg-title">Pendaftaran Perlu Pautan Ejen</h2>

                        <div className="creg-ref-missing">
                            <div className="creg-ref-missing-head">
                                <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>error</span>
                                <strong>Tiada pautan rujukan ejen</strong>
                            </div>
                            <p className="creg-ref-missing-body">
                                Pendaftaran SESAAT memerlukan pautan rujukan daripada ejen affiliate. Sila hubungi admin untuk mendapatkan pautan.
                            </p>
                            <a
                                href={`https://wa.me/${ADMIN_WA_NUMBER}?text=${encodeURIComponent(ADMIN_WA_MESSAGE)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="creg-ref-missing-btn"
                            >
                                <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>chat</span>
                                Hubungi Admin via WhatsApp
                            </a>
                        </div>

                        <button type="button" onClick={handleSignOut} className="creg-back-link">
                            <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>arrow_back</span>
                            Kembali ke Log Masuk
                        </button>
                    </>
                )}
            </div>

            <style>{`
                .creg-page {
                    min-height: 100vh;
                    display: flex; align-items: center; justify-content: center;
                    background: #0a0e1a;
                    font-family: 'Outfit', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
                    padding: 24px;
                }
                .creg-card {
                    width: 100%; max-width: 380px;
                    text-align: center;
                    animation: cregFadeUp 0.5s ease-out;
                }
                @keyframes cregFadeUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }

                .creg-branding { display: flex; flex-direction: column; align-items: center; gap: 10px; margin-bottom: 24px; }
                .creg-logo-icon {
                    width: 42px; height: 42px;
                    background: linear-gradient(135deg, #6366f1, #8b5cf6);
                    border-radius: 10px; display: flex; align-items: center; justify-content: center;
                    box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
                }
                .creg-brand-name {
                    font-size: 22px; font-weight: 800; letter-spacing: -0.5px;
                    background: linear-gradient(135deg, #93c5fd, #a5b4fc, #c4b5fd);
                    -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;
                }
                .creg-title { font-size: 18px; font-weight: 700; color: #f1f5f9; margin-bottom: 6px; }
                .creg-subtitle { font-size: 12px; color: #94a3b8; margin-bottom: 16px; line-height: 1.5; }

                .creg-form { display: flex; flex-direction: column; gap: 12px; }
                .creg-input {
                    width: 100%; padding: 13px 16px; background: rgba(51, 65, 85, 0.35);
                    border: 1px solid rgba(71, 85, 105, 0.5); border-radius: 12px; color: #f1f5f9;
                    font-size: 14px; font-weight: 500; outline: none; transition: all 0.25s ease;
                    font-family: inherit; box-sizing: border-box;
                }
                .creg-input::placeholder { color: #64748b; font-weight: 400; }
                .creg-input:focus { border-color: #3b82f6; box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15); background: rgba(51, 65, 85, 0.5); }

                .creg-submit-btn {
                    width: 100%; padding: 13px; margin-top: 2px;
                    background: linear-gradient(135deg, #3b82f6 0%, #4f46e5 60%, #6366f1 100%);
                    color: white; font-size: 14px; font-weight: 700; border: none; border-radius: 12px;
                    cursor: pointer; transition: all 0.25s ease; box-shadow: 0 4px 20px rgba(59, 130, 246, 0.35);
                    display: flex; align-items: center; justify-content: center; gap: 8px; font-family: inherit;
                }
                .creg-submit-btn:hover { box-shadow: 0 6px 30px rgba(59, 130, 246, 0.5); transform: translateY(-1px); filter: brightness(1.1); }
                .creg-submit-btn:disabled { opacity: 0.65; cursor: not-allowed; transform: none; }

                .creg-spinner {
                    width: 20px; height: 20px; border: 2.5px solid rgba(255,255,255,0.25);
                    border-top-color: white; border-radius: 50%; animation: cregSpin 0.7s linear infinite; display: inline-block;
                }
                @keyframes cregSpin { to { transform: rotate(360deg); } }

                .creg-msg-error {
                    display: flex; align-items: center; gap: 8px; padding: 10px 14px; margin-bottom: 14px;
                    border-radius: 10px; font-size: 12px; font-weight: 500; text-align: left;
                    color: #fca5a5; background: rgba(239, 68, 68, 0.12); border: 1px solid rgba(239, 68, 68, 0.25);
                }

                .creg-ref-ok {
                    display: flex; align-items: center; gap: 8px; padding: 10px 14px; margin-bottom: 14px;
                    border-radius: 10px; font-size: 12px; font-weight: 500; color: #6ee7b7;
                    background: rgba(16, 185, 129, 0.12); border: 1px solid rgba(16, 185, 129, 0.3); text-align: left;
                }
                .creg-ref-ok strong { font-weight: 700; color: #a7f3d0; letter-spacing: 0.3px; }

                .creg-ref-missing {
                    padding: 14px; margin-bottom: 14px; border-radius: 12px;
                    background: rgba(245, 158, 11, 0.10); border: 1px solid rgba(245, 158, 11, 0.35); text-align: left;
                }
                .creg-ref-missing-head { display: flex; align-items: center; gap: 8px; color: #fbbf24; font-size: 13px; margin-bottom: 6px; }
                .creg-ref-missing-body { font-size: 12px; color: #fde68a; line-height: 1.5; margin: 0 0 12px; }
                .creg-ref-missing-btn {
                    display: flex; align-items: center; justify-content: center; gap: 8px; width: 100%;
                    padding: 11px 14px; background: linear-gradient(135deg, #25d366 0%, #128c7e 100%);
                    color: white; font-size: 13px; font-weight: 700; border-radius: 10px;
                    text-decoration: none; transition: all 0.2s ease; box-shadow: 0 4px 14px rgba(37, 211, 102, 0.3);
                }
                .creg-ref-missing-btn:hover { transform: translateY(-1px); box-shadow: 0 6px 20px rgba(37, 211, 102, 0.45); }

                .creg-back-link {
                    display: flex; align-items: center; justify-content: center; gap: 6px;
                    width: 100%; margin-top: 16px; padding: 10px; background: none;
                    border: 1px solid rgba(71, 85, 105, 0.4); border-radius: 10px; color: #94a3b8;
                    font-size: 12px; font-weight: 600; cursor: pointer; transition: all 0.2s ease; font-family: inherit;
                }
                .creg-back-link:hover { border-color: rgba(100, 116, 139, 0.6); color: #cbd5e1; background: rgba(51, 65, 85, 0.2); }
            `}</style>
        </div>
    );
};

export default CompleteRegistration;
